<template>
  <div class="creator-page">
    <section class="creator-toolbar">
      <div class="creator-toolbar__copy">
        <div class="creator-toolbar__title">达人管理</div>
        <div class="creator-toolbar__desc">
          审核达人认证申请，并维护已认证达人的领域、简介、推荐位与状态。达人内容与粉丝关系复用广场数据。
        </div>
      </div>
      <div class="creator-toolbar__actions">
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
      </div>
    </section>

    <section class="creator-card">
      <div class="creator-tabs">
        <button
          class="creator-tab"
          :class="{ 'creator-tab--active': activeTab === 'applies' }"
          type="button"
          @click="switchTab('applies')"
        >
          认证申请
        </button>
        <button
          class="creator-tab"
          :class="{ 'creator-tab--active': activeTab === 'creators' }"
          type="button"
          @click="switchTab('creators')"
        >
          达人列表
        </button>
        <button
          class="creator-tab"
          :class="{ 'creator-tab--active': activeTab === 'posts' }"
          type="button"
          @click="switchTab('posts')"
        >
          达人内容
        </button>
      </div>

      <!-- 认证申请 -->
      <div v-if="activeTab === 'applies'">
        <div class="creator-filters">
          <input v-model="applyFilters.keyword" class="filter-input" type="text" placeholder="搜索：昵称 / 简介 / 理由" @keyup.enter="reload" />
          <select v-model="applyFilters.status" class="filter-select" @change="reload">
            <option value="">全部状态</option>
            <option value="PENDING">待审核</option>
            <option value="APPROVED">已通过</option>
            <option value="REJECTED">已驳回</option>
          </select>
          <button class="primary-button" type="button" @click="reload">搜索</button>
        </div>

        <div v-if="loading" class="list-state">正在加载认证申请…</div>
        <div v-else-if="!applies.length" class="list-state">没有符合条件的申请。</div>
        <div v-else class="apply-list">
          <article v-for="row in applies" :key="String(row.id)" class="apply-row">
            <div class="apply-avatar">
              <img v-if="row.avatarUrl" class="apply-avatar__img" :src="resolveFileUrl(row.avatarUrl)" alt="" />
              <div v-else class="apply-avatar__img apply-avatar__img--empty">{{ (row.nickname || "达").slice(0, 1) }}</div>
            </div>
            <div class="apply-main">
              <div class="apply-head">
                <span class="apply-name">{{ row.nickname || "用户" }}</span>
                <span class="apply-uid">UID {{ row.userId }}</span>
                <span class="tag">{{ badgeLabel(row.badgeType) }}</span>
                <span class="apply-time">{{ formatTime(row.createdAt) }}</span>
              </div>
              <div class="apply-domains" v-if="domainText(row.domainCodes)">
                <span class="tag" v-for="(d, i) in domainList(row.domainCodes)" :key="i">{{ d }}</span>
              </div>
              <div class="apply-line">
                <span class="apply-line__label">实名：</span>
                {{ row.realName || "-" }} / {{ row.idCard || "-" }}
                <span class="tag" :class="idVerifyClass(row.idVerifyStatus)">{{ idVerifyLabel(row.idVerifyStatus) }}</span>
              </div>
              <div class="apply-line">
                <span class="apply-line__label">手机号：</span>
                {{ row.phone || "-" }}
                <span v-if="row.phoneVerified" class="tag tag--ok">微信已授权</span>
              </div>
              <div class="apply-line" v-if="row.wechat"><span class="apply-line__label">微信号：</span>{{ row.wechat }}</div>
              <div class="apply-line" v-if="row.city"><span class="apply-line__label">所在城市：</span>{{ row.city }}</div>
              <div class="apply-line"><span class="apply-line__label">达人昵称：</span>{{ row.displayName || "-" }}</div>
              <div class="apply-line"><span class="apply-line__label">认证类型：</span>{{ certLabel(row.certType) }}</div>
              <div class="apply-line" v-if="materialList(row.materials).length">
                <span class="apply-line__label">证明材料：</span>
                <span v-for="(m, i) in materialList(row.materials)" :key="i">{{ m.title }}（{{ m.urls.length }}张）{{ i < materialList(row.materials).length - 1 ? " · " : "" }}</span>
              </div>
              <div class="apply-line"><span class="apply-line__label">简介：</span>{{ row.intro || "（未填写）" }}</div>
              <div class="apply-line"><span class="apply-line__label">理由：</span>{{ row.reason || "（未填写）" }}</div>
              <div class="apply-line" v-if="row.workImages"><span class="apply-line__label">作品图：</span>{{ row.workImages }}</div>
              <div class="apply-line" v-if="row.contact"><span class="apply-line__label">联系方式：</span>{{ row.contact }}</div>
              <div class="apply-line" v-if="row.sampleUrls"><span class="apply-line__label">作品：</span>{{ row.sampleUrls }}</div>
              <div class="apply-line" v-if="row.reviewNote"><span class="apply-line__label">审核备注：</span>{{ row.reviewNote }}</div>
            </div>
            <div class="apply-side">
              <span class="status-pill" :class="applyStatusClass(row.status)">{{ applyStatusLabel(row.status) }}</span>
              <button v-if="row.status === 'PENDING'" class="link-button" type="button" @click="approveApply(row)">通过</button>
              <button v-if="row.status === 'PENDING'" class="link-button link-button--danger" type="button" @click="rejectApply(row)">驳回</button>
            </div>
          </article>
        </div>

        <div v-if="total > pageSize" class="pager">
          <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
          <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
          <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
        </div>
      </div>

      <!-- 达人列表 -->
      <div v-else-if="activeTab === 'creators'">
        <div class="creator-filters">
          <input v-model="creatorFilters.keyword" class="filter-input" type="text" placeholder="搜索：昵称 / 简介 / 领域编码" @keyup.enter="reload" />
          <select v-model="creatorFilters.status" class="filter-select" @change="reload">
            <option value="">全部状态</option>
            <option value="ACTIVE">启用中</option>
            <option value="DISABLED">已停用</option>
          </select>
          <button class="primary-button" type="button" @click="reload">搜索</button>
        </div>

        <div v-if="loading" class="list-state">正在加载达人…</div>
        <div v-else-if="!creators.length" class="list-state">还没有认证达人。</div>
        <div v-else class="creator-rows">
          <article v-for="row in creators" :key="String(row.userId)" class="creator-row">
            <div class="creator-row__avatar">
              <img v-if="row.avatarUrl" class="apply-avatar__img" :src="resolveFileUrl(row.avatarUrl)" alt="" />
              <div v-else class="apply-avatar__img apply-avatar__img--empty">{{ (row.nickname || "达").slice(0, 1) }}</div>
            </div>
            <div class="creator-row__main">
              <div class="apply-head">
                <span class="apply-name">{{ row.nickname || "用户" }}</span>
                <span class="apply-uid">UID {{ row.userId }}</span>
                <span class="tag">{{ badgeLabel(row.badgeType) }}</span>
                <span class="tag" v-if="row.recommended">推荐中</span>
                <span class="status-pill" :class="row.status === 'ACTIVE' ? 'status-pill--pass' : 'status-pill--deleted'">
                  {{ row.status === "ACTIVE" ? "启用中" : "已停用" }}
                </span>
              </div>
              <div class="apply-domains" v-if="domainText(row.domainCodes)">
                <span class="tag" v-for="(d, i) in domainList(row.domainCodes)" :key="i">{{ d }}</span>
              </div>
              <div class="apply-line">{{ row.intro || "（无简介）" }}</div>
              <div class="creator-stats">
                <span>粉丝 {{ row.followerCount || 0 }}</span>
                <span>作品 {{ row.contentCount || 0 }}</span>
                <span>排序 {{ row.sortNo || 0 }}</span>
                <span>认证于 {{ formatTime(row.certifiedAt) }}</span>
              </div>
            </div>
            <div class="creator-row__side">
              <button class="link-button" type="button" @click="toggleRecommended(row)">
                {{ row.recommended ? "取消推荐" : "设为推荐" }}
              </button>
              <button class="link-button" type="button" @click="editSort(row)">改排序</button>
              <button class="link-button" type="button" @click="editDomains(row)">改领域</button>
              <button class="link-button" type="button" @click="toggleStatus(row)">
                {{ row.status === "ACTIVE" ? "停用" : "启用" }}
              </button>
              <button class="link-button link-button--danger" type="button" @click="removeCreatorRow(row)">取消认证</button>
            </div>
          </article>
        </div>

        <div v-if="total > pageSize" class="pager">
          <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
          <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
          <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
        </div>
      </div>

      <!-- 达人内容 -->
      <div v-else>
        <div class="creator-filters">
          <input v-model="postFilters.keyword" class="filter-input" type="text" placeholder="搜索：正文 / 作者昵称" @keyup.enter="reload" />
          <select v-model="postFilters.auditStatus" class="filter-select" @change="reload">
            <option value="">全部状态</option>
            <option value="PASS">已上架</option>
            <option value="REJECTED">已下架</option>
          </select>
          <select v-model="postFilters.deleted" class="filter-select" @change="reload">
            <option value="">全部内容</option>
            <option value="0">未删除</option>
            <option value="1">已删除</option>
          </select>
          <button class="primary-button" type="button" @click="reload">搜索</button>
        </div>

        <div v-if="loading" class="list-state">正在加载达人内容…</div>
        <div v-else-if="!posts.length" class="list-state">没有符合条件的达人内容。</div>
        <div v-else class="apply-list">
          <article v-for="row in posts" :key="String(row.id)" class="apply-row">
            <div class="apply-avatar">
              <img v-if="row.authorAvatarUrl" class="apply-avatar__img" :src="resolveFileUrl(row.authorAvatarUrl)" alt="" />
              <div v-else class="apply-avatar__img apply-avatar__img--empty">{{ (row.authorName || "达").slice(0, 1) }}</div>
            </div>
            <div class="apply-main">
              <div class="apply-head">
                <span class="apply-name">{{ row.authorName || "用户" }}</span>
                <span class="apply-uid">UID {{ row.ownerUserId }}</span>
                <span class="tag">{{ mediaLabel(row.mediaType) }}</span>
                <span class="apply-time">{{ formatTime(row.createdAt) }}</span>
              </div>
              <div class="apply-line" v-if="row.title"><strong>{{ row.title }}</strong></div>
              <div class="apply-line">{{ row.content || "（无正文）" }}</div>
              <div class="creator-stats">
                <span>赞 {{ row.likeCount || 0 }}</span>
                <span>评 {{ row.commentCount || 0 }}</span>
                <span>藏 {{ row.favoriteCount || 0 }}</span>
              </div>
            </div>
            <div class="apply-side">
              <span class="status-pill" :class="postStatusClass(row)">{{ postStatusLabel(row) }}</span>
              <button v-if="row.deleted === 0 && row.contentAuditStatus !== 'PASS'" class="link-button" type="button" @click="approvePost(row)">上架</button>
              <button v-if="row.deleted === 0 && row.contentAuditStatus === 'PASS'" class="link-button" type="button" @click="rejectPost(row)">下架</button>
              <button v-if="row.deleted === 0" class="link-button link-button--danger" type="button" @click="removePostRow(row)">删除</button>
            </div>
          </article>
        </div>

        <div v-if="total > pageSize" class="pager">
          <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
          <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
          <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
        </div>
      </div>

      <div v-if="message" class="page-message" :class="{ 'page-message--error': messageError }">{{ message }}</div>
    </section>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import {
  deleteCreatorPost,
  fetchCreatorApplies,
  fetchCreatorPosts,
  fetchCreators,
  removeCreator,
  reviewCreatorApply,
  updateCreator,
  updateCreatorPostStatus
} from "../api/creator"

const DOMAIN_LABELS = {
  gecko: "守宫",
  lizard: "蜥蜴",
  snake: "蛇类",
  turtle: "龟类",
  frog: "蛙类",
  spider: "蜘蛛",
  scorpion: "蝎子",
  equipment: "设备造景",
  bird: "鸟类",
  cat: "猫",
  dog: "狗",
  human: "两脚兽"
}

const BADGE_LABELS = {
  CREATOR: "个人达人",
  INSTITUTION: "机构",
  BRAND: "品牌方"
}

export default {
  name: "CreatorManageView",
  data() {
    return {
      activeTab: "applies",
      loading: false,
      applies: [],
      creators: [],
      posts: [],
      total: 0,
      page: 1,
      pageSize: 20,
      applyFilters: { keyword: "", status: "PENDING" },
      creatorFilters: { keyword: "", status: "" },
      postFilters: { keyword: "", auditStatus: "", deleted: "" },
      message: "",
      messageError: false
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    }
  },
  created() {
    this.reload()
  },
  methods: {
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    formatTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    badgeLabel(type) {
      return BADGE_LABELS[type] || "个人达人"
    },
    mediaLabel(type) {
      if (type === "video") return "视频"
      if (type === "image") return "图片"
      return "文字"
    },
    postStatusLabel(row) {
      if (row.deleted === 1) return "已删除"
      if (row.contentAuditStatus === "PASS") return "已上架"
      return "已下架"
    },
    postStatusClass(row) {
      if (row.deleted === 1) return "status-pill--deleted"
      if (row.contentAuditStatus === "PASS") return "status-pill--pass"
      return "status-pill--rejected"
    },
    domainText(codes) {
      return this.domainList(codes).join(" / ")
    },
    domainList(codes) {
      if (!codes) return []
      return String(codes)
        .split(",")
        .map(code => code.trim())
        .filter(Boolean)
        .map(code => DOMAIN_LABELS[code] || code)
    },
    applyStatusLabel(status) {
      if (status === "APPROVED") return "已通过"
      if (status === "REJECTED") return "已驳回"
      return "待审核"
    },
    applyStatusClass(status) {
      if (status === "APPROVED") return "status-pill--pass"
      if (status === "REJECTED") return "status-pill--rejected"
      return "status-pill--reviewing"
    },
    idVerifyLabel(status) {
      if (status === "VERIFIED") return "实名已核验"
      if (status === "REJECTED") return "实名未通过"
      return "待人工核验"
    },
    idVerifyClass(status) {
      if (status === "VERIFIED") return "tag--ok"
      if (status === "REJECTED") return "tag--bad"
      return ""
    },
    certLabel(type) {
      if (type === "BREEDER") return "繁育者"
      if (type === "VET") return "执业兽医"
      if (type === "MERCHANT") return "实体商家"
      if (type === "PLAYER") return "资深玩家"
      return "-"
    },
    materialList(json) {
      if (!json) return []
      try {
        const parsed = JSON.parse(json)
        if (!Array.isArray(parsed)) return []
        return parsed.map(item => ({
          title: item.title || item.key || "材料",
          urls: Array.isArray(item.urls) ? item.urls : []
        }))
      } catch (e) {
        return []
      }
    },
    setMessage(text, isError) {
      this.message = text
      this.messageError = !!isError
    },
    switchTab(tab) {
      this.activeTab = tab
      this.page = 1
      this.reload()
    },
    async reload() {
      this.loading = true
      try {
        if (this.activeTab === "applies") {
          const res = await fetchCreatorApplies({
            keyword: this.applyFilters.keyword || undefined,
            status: this.applyFilters.status || undefined,
            page: this.page,
            pageSize: this.pageSize
          })
          const data = res.data || {}
          this.applies = data.records || []
          this.total = Number(data.total || 0)
        } else if (this.activeTab === "creators") {
          const res = await fetchCreators({
            keyword: this.creatorFilters.keyword || undefined,
            status: this.creatorFilters.status || undefined,
            page: this.page,
            pageSize: this.pageSize
          })
          const data = res.data || {}
          this.creators = data.records || []
          this.total = Number(data.total || 0)
        } else {
          const res = await fetchCreatorPosts({
            keyword: this.postFilters.keyword || undefined,
            auditStatus: this.postFilters.auditStatus || undefined,
            deleted: this.postFilters.deleted === "" ? undefined : Number(this.postFilters.deleted),
            page: this.page,
            pageSize: this.pageSize
          })
          const data = res.data || {}
          this.posts = data.records || []
          this.total = Number(data.total || 0)
        }
      } catch (error) {
        this.setMessage((error && error.message) || "加载失败", true)
      } finally {
        this.loading = false
      }
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.reload()
    },
    async approveApply(row) {
      try {
        const res = await reviewCreatorApply(row.id, "approve")
        this.setMessage(res.message || "已通过认证", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async rejectApply(row) {
      const note = window.prompt("驳回原因（可选）：", "")
      if (note === null) return
      try {
        const res = await reviewCreatorApply(row.id, "reject", note)
        this.setMessage(res.message || "已驳回申请", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async toggleRecommended(row) {
      try {
        await updateCreator(row.userId, {
          recommended: !row.recommended
        })
        this.setMessage(row.recommended ? "已取消推荐" : "已设为推荐", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async editSort(row) {
      const value = window.prompt("推荐排序（越小越靠前）：", String(row.sortNo || 0))
      if (value === null) return
      const sortNo = Number(value)
      if (Number.isNaN(sortNo)) {
        this.setMessage("排序必须是数字", true)
        return
      }
      try {
        await updateCreator(row.userId, { sortNo })
        this.setMessage("排序已更新", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async editDomains(row) {
      const value = window.prompt("领域编码，用英文逗号分隔（如 gecko,snake）：", row.domainCodes || "")
      if (value === null) return
      try {
        await updateCreator(row.userId, { domainCodes: value })
        this.setMessage("领域已更新", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async toggleStatus(row) {
      try {
        await updateCreator(row.userId, {
          status: row.status === "ACTIVE" ? "DISABLED" : "ACTIVE"
        })
        this.setMessage(row.status === "ACTIVE" ? "已停用" : "已启用", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async removeCreatorRow(row) {
      if (!window.confirm(`确定取消「${row.nickname || row.userId}」的达人认证吗？取消后该用户会从达人页移除。`)) {
        return
      }
      try {
        await removeCreator(row.userId)
        this.setMessage("已取消达人认证", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async approvePost(row) {
      try {
        await updateCreatorPostStatus(row.id, "PASS")
        this.setMessage("内容已上架", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async rejectPost(row) {
      try {
        await updateCreatorPostStatus(row.id, "REJECTED")
        this.setMessage("内容已下架", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async removePostRow(row) {
      if (!window.confirm(`确定删除达人内容 #${row.id} 吗？`)) {
        return
      }
      try {
        await deleteCreatorPost(row.id)
        this.setMessage("内容已删除", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    }
  }
}
</script>

<style scoped>
.creator-page {
  padding: 20px;
}

.creator-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.creator-toolbar__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.creator-toolbar__desc {
  margin-top: 6px;
  max-width: 720px;
  font-size: 13px;
  line-height: 1.6;
  color: #6b7280;
}

.creator-card {
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}

.creator-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.creator-tab {
  padding: 8px 18px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #4b5563;
  cursor: pointer;
  font-size: 14px;
}

.creator-tab--active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.creator-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
}

.apply-list,
.creator-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.apply-row,
.creator-row {
  display: flex;
  gap: 14px;
  padding: 14px;
  border: 1px solid #eef0f3;
  border-radius: 12px;
  background: #fcfcfd;
}

.apply-avatar {
  flex-shrink: 0;
}

.apply-avatar__img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.apply-avatar__img--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: #2563eb;
  font-weight: 700;
}

.apply-main,
.creator-row__main {
  flex: 1;
  min-width: 0;
}

.apply-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.apply-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.apply-uid,
.apply-time {
  font-size: 12px;
  color: #9ca3af;
}

.apply-domains {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.apply-line {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.6;
  color: #4b5563;
  word-break: break-word;
}

.apply-line__label {
  color: #9ca3af;
}

.creator-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

.apply-side,
.creator-row__side {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.creator-row__avatar {
  flex-shrink: 0;
}

.tag--ok {
  background: #e7f4e7;
  color: #2f7d3c;
  border-color: #bfe3c3;
}

.tag--bad {
  background: #fdecec;
  color: #c0392b;
  border-color: #f3c6c1;
}
</style>
